/**
 * Company store — leaf module (Perf 3.1.b Step 5). Cross-store refs via runtime getStores().
 */
import { create } from 'zustand';
import { companiesApi } from '@/lib/api/companies';

const getStores = () => require('@/store');

const useCompanyStore = create((set, get) => ({
  // State
  companies: {},
  companiesWithModelIds: [], // Array of company ids with their top model ids
  topModelsByCompany: {}, // Map of company id to array of top model ids
  isLoadingCompanies: false,
  isLoadingCompaniesByModel: false,
  isFetchedCompanies: false,
  companiesError: null,
  companiesByModelError: null,
  companiesByModelPagination: {
    current: 1,
    pages: 1,
    total: 0,
    perPage: 10,
    hasMore: false
  },

  // Helper action to transform and add companies
  addCompanies: (newCompanies) => {
    if (!Array.isArray(newCompanies)) {
      newCompanies = [newCompanies];
    }
    
    const processedCompanies = newCompanies.map(company => ({
      id: company.id,
      name: company.name,
      slug: company.slug,
      description: company.description,
      website: company.website,
      attribution: company.attribution,
      logoPath: company.logoPath,
      flags: {
        isEnabled: company?.flags?.isEnabled ?? true
      },
      createdAt: company.createdAt,
      updatedAt: company.updatedAt
    }));
    
    set(state => ({
      companies: {
        ...state.companies,
        ...Object.fromEntries(processedCompanies.map(company => [company.id, company]))
      }
    }));

    return processedCompanies;
  },

  // Fetch companies with their top models
  fetchModelsByCompanies: async (page = 1, limit = 20, modelsLimit = 9) => {
    try {
      set({ 
        isLoadingCompaniesByModel: true, 
        companiesByModelError: null 
      });

      const { companies, pagination } = await companiesApi.getModelsByCompanies(page, limit, modelsLimit);
      
      // Process all models first
      const allModels = companies.flatMap(company => company.topModels || []);

      getStores().useModelStore.getState().addModels(allModels);

      // Process companies and store relationships
      const processedCompanies = get().addCompanies(companies);
      
      // Store the relationships
      const topModelsByCompany = {};
      companies.forEach(company => {
        topModelsByCompany[company.id] = (company.topModels || []).map(model => model.id);
      });

      set(state => ({ 
        companiesWithModelIds: companies.map(company => ({
          id: company.id,
          modelIds: (company.topModels || []).map(model => model.id)
        })),
        topModelsByCompany,
        companiesByModelPagination: pagination,
        isLoadingCompaniesByModel: false
      }));

      return processedCompanies;
    } catch (error) {
      set({ 
        companiesByModelError: error.message || 'Failed to fetch companies with models',
        isLoadingCompaniesByModel: false 
      });
      throw error;
    }
  },

  // Get top models for a company from store
  getTopModelsByCompany: (companyId) => {
    const modelIds = get().topModelsByCompany[companyId] || [];
    return modelIds.map(id => getStores().useModelStore.getState().getModelById(id)).filter(Boolean);
  },

  // Clear companies with models
  clearModelsByCompany: () => {
    set({
      companiesWithModelIds: [],
      topModelsByCompany: {},
      companiesByModelPagination: {
        current: 1,
        pages: 1,
        total: 0,
        perPage: 10,
        hasMore: false
      },
      isLoadingCompaniesByModel: false,
      companiesByModelError: null
    });
  },

  // Actions
  // De-duped at the store level so concurrent callers (e.g. the models
  // catalog page kicking off both a generic bootstrap and a deep-link
  // `?company=` effect on the same render) collapse onto a single in-
  // flight request instead of double-firing `/json/models/companies`.
  fetchModelCompanies: async () => {
    if (get().isFetchedCompanies) return;
    if (get()._fetchModelCompaniesPromise) return get()._fetchModelCompaniesPromise;

    const promise = (async () => {
      try {
        set({ isLoadingCompanies: true, companiesError: null });
        const companies = await companiesApi.getModelCompanies();
        get().addCompanies(companies);
        set({ isLoadingCompanies: false, isFetchedCompanies: true });
      } catch (error) {
        set({
          companiesError: error.message || 'Failed to fetch companies',
          isLoadingCompanies: false,
        });
        throw error;
      } finally {
        set({ _fetchModelCompaniesPromise: null });
      }
    })();

    set({ _fetchModelCompaniesPromise: promise });
    return promise;
  },

  // Selectors
  getCompanyById: (id) => get().companies[id],
  
  getAllCompanies: () => Object.values(get().companies),
  
  // Reset state
  clearCompanies: () => {
    set({
      companies: {},
      companiesWithModelIds: [],
      topModelsByCompany: {},
      isLoadingCompanies: false,
      isLoadingCompaniesByModel: false,
      isFetchedCompanies: false,
      companiesError: null,
      companiesByModelError: null,
      companiesByModelPagination: {
        current: 1,
        pages: 1,
        total: 0,
        perPage: 10,
        hasMore: false
      }
    });
  },

  // Fetch models for a specific company
  fetchModelsByCompany: async (companyId, page = 1) => {
    try {
      set({ isLoadingCompaniesByModel: true, companiesByModelError: null });

      const data = await companiesApi.getModelsByCompany(companyId, page);
      if (!data) {
        throw new Error('Failed to fetch models for company');
      }
      const { company, models, pagination } = data;

      // Add the company to the store
      get().addCompanies(company);

      // Add models to the models store
      getStores().useModelStore.getState().addModels(models);

      // Store the relationships
      set(state => ({
        topModelsByCompany: {
          ...state.topModelsByCompany,
          [companyId]: models.map(model => model.id)
        },
        companiesByModelPagination: pagination,
        isLoadingCompaniesByModel: false
      }));

      return models;
    } catch (error) {
      set({ 
        companiesByModelError: error.message || 'Failed to fetch models for company',
        isLoadingCompaniesByModel: false 
      });
      throw error;
    }
  },
}));

export { useCompanyStore };
export default useCompanyStore;
